<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { PersonsOfInterestEmbeds } from '@/Pejer/tables/PersonsOfInterest'
import { QueryClient } from '@tanstack/vue-query'
import { Link, Mail, SignatureIcon } from 'lucide-vue-next'
import BasicEditModal from 'pejer/src/components/Modal/BasicEditModal.vue'
import { Config } from 'pejer/src/components/ui/auto-form'
import { Button } from 'pejer/src/components/ui/button'
import { toast } from 'pejer/src/components/ui/toast'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'pejer/src/components/ui/tooltip'
import { z } from 'zod'

const props = defineProps<{
  personOfInterest: PersonsOfInterestEmbeds.ListItem
}>()

const sendEmail = () => {
  const emailBody = `Hei ${props.personOfInterest.name},

For å fortsatt benytte betalingsløsningen via ${props.personOfInterest.platformName} må signaturberretighede i ${props.personOfInterest.businessName} signere hos betalingsleverandøren Bucky Payment AS. Du er registrert som signaturberretighed og vi trenger derfor at du trykker på linken under for å signere.

Trykk her for å signere med BankID: ${props.personOfInterest.signatureUrl}

Hvorfor må vi signere?
Aktører som utfører betalinger, herunder Bucky Payment AS som utfører betalinger på vegne av ${props.personOfInterest.businessName}, er underlagt lov om hvitvasking for å forebygge at deres betalingstjenester blir benyttet som ett ledd i økonomisk kriminalitet. Dette gjør at aktører er pålagt å gjennomføre årlige kontroller av alle brukere for å sikre at korrekte opplysninger er registrert.

Vennligst bekreft brukervilkår og e-signer de opplysningene som er registret for din organisasjon
`

  const subject = `Signering kreves`
  const mailToLink = `mailto:${props.personOfInterest.signatureEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`
  window.open(mailToLink, '_blank')
}

const copyLink = () => {
  navigator.clipboard.writeText(props.personOfInterest.signatureUrl)
  toast({
    title: 'Link kopiert',
    description: 'Linken er kopiert til utklippstavlen',
  })
}
const pejer = usePejer()

const formSchema = z.object({
  signatureEmail: z.string().email('Må være en gyldig e-postadresse'),
  // isForcedRed: z.boolean().default(false).optional(),
})

const fieldConfig: Config<z.infer<typeof formSchema>> = {
  signatureEmail: {
    label: 'Signerings e-post',
    description: 'E-posten som signeringslinken sendes til',
    component: 'string',
  },
}
const onSuccess = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['personsOfInterest'] })
  queryClient.invalidateQueries({ queryKey: ['connectedAccounts'] })
  queryClient.invalidateQueries({ queryKey: ['businesses'] })
}
</script>
<template>
  <div
    v-if="personOfInterest.signatureUrl !== '#' && !personOfInterest.signedAt"
    class="flex gap-2"
  >
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <a :href="personOfInterest.signatureUrl" target="_blank">
            <Button variant="outline" class="p-2 bg-slate-100 hover:scale-105 hover:bg-white gap-2">
              Signer <SignatureIcon :size="16" />
            </Button>
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>Klikk for å signere selv</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            @click.prevent.stop="copyLink"
            variant="outline"
            class="p-2 bg-slate-100 hover:scale-105 hover:bg-white gap-2"
          >
            Kopier <Link :size="16" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Kopier signeringslinken</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            @click.prevent.stop="sendEmail"
            variant="default"
            class="p-2 hover:scale-[102%] transition-all gap-2"
          >
            Påminn <Mail :size="16" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Send signeringslinken på mail</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
  <div v-else-if="!personOfInterest.signatureRequired" class="flex items-center gap-2">
    <BasicEditModal
      :field-config="fieldConfig"
      :validation-schema="formSchema"
      :initial-value="{
        signatureRequired: !!personOfInterest.signatureRequired,
        signatureEmail: personOfInterest.signatureEmail || '',
      }"
      :submit="
        (value) =>
          pejer.fetch(pejer.urls.personsOfInterest.requireSignature, {
            pathArgs: { id: personOfInterest.id },
            body: {
              requireSignature: !personOfInterest.signatureRequired,
              signatureEmail: value.signatureEmail,
            },
          })
      "
      :url="pejer.urls.personsOfInterest.requireSignature"
      @onSuccess="onSuccess"
      cancel-text="Avbryt"
      submit-text="Krev signatur"
    >
      <template #title> Krev signatur av {{ personOfInterest.name }} </template>
      <template #description><div></div></template>
      <template #asChild>
        <Button
          @click.prevent.stop
          variant="outline"
          class="gap-2 bg-slate-100 hover:bg-white hover:scale-[102%] transition-all"
        >
          Krev signatur <SignatureIcon :size="16" />
        </Button>
      </template>
    </BasicEditModal>
  </div>
</template>
